'use client';

import BDButton from '@shared/components/elements/button';
import { useActionState, useRef, useState } from 'react';

import { Checkbox, Field, Fieldset, Input, Label } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { createCustomerAction, signInShopify } from '@shared/components/customer/customer-actions';
import BDInputField from '@shared/components/elements/bd-input';
import { BDStyledDialog } from '@shared/components/elements/dialog';
import { GOLD_CLUB_INFO } from '@shared/lib/constants';
import { withSignupTracking } from '@shared/lib/utils/conversion-tracking';
import { rPercentage } from '@shared/lib/utils/formats';

export default function SignupForm({
  cancel,
  afterSubmit,
}: {
  cancel?: () => void;
  afterSubmit?: () => void;
}) {
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [promoChecked, setPromoChecked] = useState(true);
  const [afterSignupDialogOpen, setAfterSignupDialogOpen] = useState(false);

  const [formErrorMessage, formAction, pending] = useActionState(
    async (prevState: string | null, formData: FormData) => {
      const errorMessage = await createCustomerAction(prevState, formData);
      if (errorMessage) {
        return errorMessage;
      }
      // TODO: Add afterSubmit callback, right now it closes the dialog and prevents the after submit dialog from being shown
      setAfterSignupDialogOpen(true);
      return null;
    },
    null
  );
  const trackedFormAction = withSignupTracking(formAction);

  const handleFormChange = () => {
    // Check form validity
    if (formRef.current) {
      setIsFormValid(formRef.current.checkValidity());
    }
  };
  return (
    <>
      <form
        ref={formRef}
        onChange={handleFormChange}
        action={trackedFormAction}
        className="mt-4 w-full max-w-md"
      >
        <Fieldset className="group grid grid-cols-2 gap-x-4 pb-4 text-base">
          <BDInputField
            id="firstName"
            type="text"
            label="First name"
            errorMessage="Please enter your first name."
            required
            pattern="[a-zA-ZÀ-ÿ\-]{2,}"
            className="my-4"
          />
          <BDInputField
            id="lastName"
            type="text"
            label="Last name"
            errorMessage="Please enter your last name."
            required
            pattern="[a-zA-ZÀ-ÿ\-]{2,}"
            className="my-4"
          />
          <BDInputField
            id="email"
            type="email"
            label="Email address"
            errorMessage="Not a valid email address."
            required
            className="col-span-2 my-4"
          />

          <Field
            key="promoConsent"
            className="relative col-span-2 mb-4 mt-1 flex items-center gap-1"
          >
            <Input type="hidden" name="promoChecked" value={promoChecked.toString()}></Input>
            <Checkbox
              checked={promoChecked}
              onChange={setPromoChecked}
              name="signup-promotional-email"
              className="data-checked:bg-bd-primary group peer block size-4 rounded-sm border bg-white"
            >
              <svg
                className="group-data-checked:opacity-100 stroke-white opacity-0"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M3 8L6 11L11 3.5"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Checkbox>
            <Label>Agree to receive occasional promotional email.</Label>
            <p
              id="promo-error"
              className="peer-data-checked:hidden absolute top-full w-full text-xs text-red-400"
            >
              Occasional promotional emails sign up required to get this offer.
            </p>
          </Field>
        </Fieldset>
        <div className="relative flex flex-row items-center justify-center gap-4">
          {cancel && (
            <BDButton styleType="secondary" className="w-64 py-2" onClick={cancel}>
              Cancel
            </BDButton>
          )}
          <BDButton
            styleType="primary"
            data-autofocus
            type="submit"
            disabled={!isFormValid || pending || !promoChecked}
            aria-busy={pending}
            pending={pending}
            disabledMessage="Please fill out all fields and agree to receive occasional promotional emails."
            className="w-64 py-2"
          >
            Sign up
          </BDButton>
        </div>
        {formErrorMessage && (
          <p aria-live="polite" role="status" className="text-xxs text-red-400">
            {formErrorMessage}
          </p>
        )}
      </form>
      {/* After signup dialog */}
      <BDStyledDialog
        openButton={<></>}
        open={afterSignupDialogOpen}
        onClose={() => setAfterSignupDialogOpen(false)}
        imageOrIcon={
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <CheckIcon aria-hidden="true" className="w-1/2 text-green-600" />
          </div>
        }
        title="Thank you for signing up!"
        description={
          <span>
            You are now a member of the Bonded Diamond™ Club. You will receive a welcome email
            shortly. To enjoy your{' '}
            <span className="underline">{rPercentage(GOLD_CLUB_INFO.discount)} off</span> discount,
            please sign in to your account.
          </span>
        }
      >
        <form action={signInShopify} className="grid grid-cols-2 gap-4">
          <BDButton
            styleType="secondary"
            className="w-full px-4 py-2"
            type="button"
            onClick={() => {
              setAfterSignupDialogOpen(false);
            }}
          >
            Cancel
          </BDButton>
          <BDButton styleType="primary" className="w-full px-4 py-2" type="submit">
            Sign in
          </BDButton>
        </form>
      </BDStyledDialog>
    </>
  );
}
